/**
 * Comparison-таблица для секции «Кто наш пользователь» (`narr.canvas.audience`)
 * когда в Канвасе ≥3 Сегментов.
 *
 * При 1–2 Сегментах вертикальный stack NarrativeBlockView'ов читается
 * нормально; при ≥3 каждое описание сегмента занимает по странице и
 * сравнить параметры между сегментами невозможно. Comparison-table
 * решает обе проблемы: одна строка = один параметр, колонки = сегменты.
 *
 * Mobile: горизонтальный scroll внутри ScrollArea. Заголовок-сегмент
 * остаётся кликабельным (открывает EntityCard drawer с DSL).
 */

import { Badge, Group, ScrollArea, Table, Text, UnstyledButton } from '@mantine/core';
import type { NarrativeBlock } from '@pflow/shared/v2';
import { useUiStore } from '../stores/ui-store';

interface Props {
    blocks: NarrativeBlock[];
}

export function AudienceTable({ blocks }: Props): JSX.Element {
    const openCard = useUiStore(s => s.openCard);

    // Собираем уникальные имена полей в порядке первого появления, чтобы
    // строки таблицы шли так же, как они объявлены в первом сегменте.
    const fieldKeys: string[] = [];
    const seenKey = new Set<string>();
    for (const b of blocks) {
        for (const f of b.fields ?? []) {
            if (!seenKey.has(f.key)) {
                seenKey.add(f.key);
                fieldKeys.push(f.key);
            }
        }
    }

    function valueOf(b: NarrativeBlock, key: string): string {
        const f = (b.fields ?? []).find(x => x.key === key);
        return f ? String(f.value) : '—';
    }

    return (
        <ScrollArea type="auto" offsetScrollbars>
            <Table
                striped
                withTableBorder
                withColumnBorders
                verticalSpacing="xs"
                horizontalSpacing="sm"
                style={{ fontSize: 13 }}
            >
                <Table.Thead>
                    <Table.Tr>
                        {/* Угловая ячейка: пустая, чтобы первая колонка
                           «параметр» не нуждалась в подписи. */}
                        <Table.Th style={{ minWidth: 110, width: 110 }} />
                        {blocks.map((b, i) => (
                            <Table.Th key={i} style={{ minWidth: 180 }}>
                                <SegmentHeader block={b} onClick={() => b.entity && openCard(b.entity)} />
                            </Table.Th>
                        ))}
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {fieldKeys.map(key => (
                        <Table.Tr key={key}>
                            <Table.Td>
                                <Text fz="xs" c="dimmed">{key}</Text>
                            </Table.Td>
                            {blocks.map((b, i) => (
                                <Table.Td key={i}>
                                    <Text fz="sm" style={{ wordBreak: 'break-word' }}>
                                        {valueOf(b, key)}
                                    </Text>
                                </Table.Td>
                            ))}
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        </ScrollArea>
    );
}

function SegmentHeader({ block, onClick }: { block: NarrativeBlock; onClick: () => void }): JSX.Element {
    const name = block.entity?.name ?? block.label ?? '';
    const citationCount = block.citations?.length ?? 0;
    const healthCount   = block.health?.length ?? 0;
    return (
        <UnstyledButton
            onClick={onClick}
            disabled={!block.entity}
            className={block.entity ? 'pflow-entity-trigger' : undefined}
            style={{
                display: 'block',
                width: '100%',
                padding: '2px 4px',
                margin: '-2px -4px',
                borderRadius: 4,
            }}
        >
            <Text fw={600} fz="sm" style={{ wordBreak: 'break-word' }}>«{name}»</Text>
            {(citationCount > 0 || healthCount > 0) && (
                <Group gap={4} mt={2}>
                    {citationCount > 0 && (
                        <Badge size="xs" variant="light" color="blue">📡 {citationCount}</Badge>
                    )}
                    {healthCount > 0 && (
                        <Badge size="xs" variant="light" color="yellow">⚠ {healthCount}</Badge>
                    )}
                </Group>
            )}
        </UnstyledButton>
    );
}
